import { createRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input } from '@cca/ui';
import { MailCheck } from 'lucide-react';
import { useSession } from '../lib/session.js';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { rootRoute } from './root.js';

/**
 * Passwordless sign-in (magic link).
 *
 * Chosen over passwords deliberately: this is an internal staff tool where the
 * mailbox is already the trust anchor, and a password is one more secret to
 * phish, reuse or leak. Nothing secret is typed into this form at all.
 *
 * Registration is the same flow. That is safe because authenticating is not
 * authorisation: a brand-new auth user has no organisation membership, so
 * `resolvePrincipal` returns `no_membership` and every API call 403s until an
 * administrator grants access. Signing in proves you own an inbox; it does not
 * grant you a tenant.
 */
function SignInPage() {
  const config = useRuntimeConfig();
  const { client, session, loading } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = useRouterState({
    select: (s) => (s.location.search as { redirect?: string }).redirect ?? '/',
  });

  // The magic link returns to the app with the token in the URL fragment;
  // the Supabase client is configured with detectSessionInUrl and picks it up,
  // which lands here as a session.
  useEffect(() => {
    if (session) void navigate({ to: redirectTo });
  }, [session, navigate, redirectTo]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client) return;

    setSubmitting(true);
    setError(null);

    const { error: otpError } = await client.auth.signInWithOtp({
      email: email.trim(),
      options: {
        // Creating the auth user on first sign-in is what makes this both
        // "sign in" and "register". Access still requires a membership.
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}${redirectTo}`,
      },
    });

    setSubmitting(false);

    if (otpError) {
      setError(otpError.message);
      return;
    }

    // Always the same confirmation, whether or not the address is known — the
    // screen must not become an account-existence oracle.
    setSentTo(email.trim());
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <p className="text-sm text-slate-600 dark:text-slate-400">Checking session…</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="mx-auto flex min-h-full max-w-md items-center p-6">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Authentication is not configured</CardTitle>
            <CardDescription>
              This environment has no Supabase connection, so there is nothing to sign in to.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Set <code>PUBLIC_SUPABASE_URL</code> and <code>PUBLIC_SUPABASE_ANON_KEY</code>, then
              restart the worker. Locally, run <code>pnpm db:start</code> and copy the values from{' '}
              <code>supabase status</code>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sentTo) {
    return (
      <div className="mx-auto flex min-h-full max-w-md items-center p-6">
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <MailCheck className="h-5 w-5 text-emerald-600" aria-hidden="true" />
              <CardTitle>Check your inbox</CardTitle>
            </div>
            <CardDescription>
              If <strong>{sentTo}</strong> can sign in, a link is on its way. It expires shortly and
              can be used once.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Open the link on this device to complete sign-in.
            </p>

            {config.simulatorEnabled ? (
              <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                Development simulator: no mail leaves this machine. The local Supabase stack
                captures it — open Mailpit (shown by <code>supabase status</code>) to click the
                link.
              </p>
            ) : null}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setSentTo(null);
                setError(null);
              }}
            >
              Use a different address
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md items-center p-6">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Sign in to {config.product.name}</CardTitle>
          <CardDescription>
            We email you a link. No password to remember, and nothing secret to type.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="email" className="block text-sm font-medium">
                Work email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            {error ? (
              <p role="alert" className="text-sm text-red-700 dark:text-red-400">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={submitting || email.trim() === ''}>
              {submitting ? 'Sending…' : 'Email me a sign-in link'}
            </Button>
          </form>

          <p className="mt-4 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            First time here? The same link registers you. You will still need an administrator to
            add you to an organisation before you can see any data.
          </p>

          {config.simulatorEnabled ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Development simulator: <code>pnpm demo:seed</code> creates{' '}
              <code>owner@demo.test</code>.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});
